import React, { useState, useEffect } from 'react';
import { packages as mockPackages } from '../mockData';
import { Clock, Users, DollarSign, Leaf, ArrowRight, MapPin, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { packagesAPI } from '../services/api';
import ShareButton from '../components/ShareButton';

const Packages = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const data = await packagesAPI.getAll();
        setPackages(data);
      } catch (error) {
        console.error('Error fetching packages:', error);
        // Fallback to mock data
        setPackages(mockPackages);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  const handleLearnMore = (pkg) => {
    setSelectedPackage(pkg);
    setIsDialogOpen(true);
  };

  const getPackageUrl = (pkg) => {
    return `${window.location.origin}/packages?id=${pkg.id}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-24 pt-40 relative overflow-hidden">
        {/* Logo Icon Watermark */}
        <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
          <img 
            src="https://customer-assets.emergentagent.com/job_conscious-escape/artifacts/o5c63p3f_customcolor_icon_transparent_background.png" 
            alt="" 
            className="w-[500px] h-auto"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-5xl sm:text-6xl font-black mb-6">
            TRAVEL <span className="text-[#ffb84d]">PACKAGES</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Curated adventures across Latin America & the Caribbean. Every trip is eco-certified.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm font-bold">
            <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full">
              <Leaf className="text-[#84bc00]" size={16} />
              <span>100% ECO-CERTIFIED</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full">
              <Users className="text-[#84bc00]" size={16} />
              <span>SMALL GROUPS</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full">
              <Check className="text-[#84bc00]" size={16} />
              <span>ALL-INCLUSIVE OPTIONS</span>
            </div>
          </div>
        </div>
      </div>

      {/* Packages Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg">Loading packages...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <div
              key={pkg.id}
              className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:scale-105 border-2 border-transparent hover:border-[#84bc00] animate-fade-in-up cursor-pointer"
              style={{ animationDelay: `${index * 100}ms` }}
              onClick={() => handleLearnMore(pkg)}
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {pkg.sustainable && (
                  <div className="absolute top-4 left-4 bg-[#84bc00] text-black font-bold text-xs px-3 py-2 rounded-full shadow-lg flex items-center space-x-1">
                    <Leaf size={14} />
                    <span>ECO-CERTIFIED</span>
                  </div>
                )}
                {pkg.spots <= 10 && (
                  <div className="absolute top-4 right-4 bg-[#ffb84d] text-black font-bold text-xs px-3 py-2 rounded-full shadow-lg animate-pulse">
                    ONLY {pkg.spots} SPOTS LEFT!
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <Badge className="bg-[#ffb84d]/10 text-[#ffb84d] border-[#ffb84d] font-bold">
                    {pkg.category}
                  </Badge>
                  <div className="flex items-center space-x-1 text-sm text-gray-600">
                    <MapPin size={14} className="text-[#84bc00]" />
                    <span className="font-semibold">{pkg.location}</span>
                  </div>
                </div>
                <h3 className="text-2xl font-black text-black mb-3 group-hover:text-[#84bc00] transition-colors duration-300">
                  {pkg.name}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-2">{pkg.description}</p>

                {/* Highlights */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {pkg.highlights.slice(0, 3).map((highlight, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-semibold">
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-2 mb-6">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Clock size={16} className="text-[#84bc00]" />
                    <span className="font-semibold">{pkg.duration}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Users size={16} className="text-[#84bc00]" />
                    <span className="font-semibold">{pkg.groupSize}</span>
                  </div>
                </div>

                {/* Price and CTA */}
                <div className="pt-6 border-t border-gray-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center space-x-2">
                        <DollarSign size={20} className="text-[#84bc00]" />
                        <span className="text-3xl font-black text-black">{pkg.price}</span>
                      </div>
                      <span className="text-xs text-gray-500">per person</span>
                    </div>
                    <a href="#" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); handleLearnMore(pkg); }}>
                      <Button className="bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold transition-all duration-300 hover:scale-105 group">
                        <span>LEARN MORE</span>
                        <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                      </Button>
                    </a>
                  </div>
                  
                  {/* Share Button */}
                  <div className="flex justify-end">
                    <ShareButton 
                      title={pkg.name}
                      description={pkg.description}
                      url={getPackageUrl(pkg)}
                      image={pkg.image}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Package Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          {selectedPackage && (
            <ScrollArea className="max-h-[80vh] pr-4">
              <DialogHeader>
                <DialogTitle className="text-3xl font-black text-[#84bc00]">
                  {selectedPackage.name}
                </DialogTitle>
                <DialogDescription className="text-lg text-gray-600">
                  {selectedPackage.location} • {selectedPackage.duration}
                </DialogDescription>
              </DialogHeader>

              {/* Image */}
              <div className="mt-6 rounded-xl overflow-hidden">
                <img src={selectedPackage.image} alt={selectedPackage.name} className="w-full h-64 object-cover" />
              </div>

              {/* Description */}
              <div className="mt-6">
                <h3 className="text-xl font-bold text-black mb-3">About This Trip</h3>
                <p className="text-gray-700 leading-relaxed">{selectedPackage.description}</p>
              </div>

              {/* Highlights */}
              <div className="mt-6">
                <h3 className="text-xl font-bold text-black mb-3">Trip Highlights</h3>
                <div className="grid grid-cols-2 gap-3">
                  {selectedPackage.highlights.map((highlight, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Check className="text-[#84bc00] flex-shrink-0" size={18} />
                      <span className="text-gray-700 font-semibold">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What's Included */}
              <div className="mt-6">
                <h3 className="text-xl font-bold text-black mb-3">What's Included</h3>
                <div className="space-y-2">
                  {selectedPackage.included.map((item, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <Leaf className="text-[#84bc00] flex-shrink-0" size={18} />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price and Book */}
              <div className="mt-8 bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center space-x-2">
                      <DollarSign size={24} className="text-[#84bc00]" />
                      <span className="text-4xl font-black text-black">{selectedPackage.price}</span>
                    </div>
                    <span className="text-sm text-gray-500">per person • Only {selectedPackage.spots} spots left</span>
                  </div>
                  <a href="/consultation" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-[#84bc00] hover:bg-[#6a9600] text-black font-bold text-lg px-8 py-6 transition-all duration-300 hover:scale-105">
                      BOOK NOW
                    </Button>
                  </a>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Packages;
